import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  Check, 
  X, 
  ArrowRight
} from 'lucide-react';
import Button from '../components/Button';
import PricingCard from '../components/PricingCard';

const Pricing = () => {
  const plans = [
    {
      name: 'Starter',
      price: '999',
      period: 'month',
      description: 'Perfect for small businesses getting started with digital marketing',
      isPopular: false,
      features: [
        { text: 'Social Media Management (3 platforms)', included: true },
        { text: 'Content Creation (10 posts/month)', included: true },
        { text: 'Basic Analytics & Reporting', included: true },
        { text: 'Email Support', included: true },
        { text: 'Monthly Strategy Call', included: true },
        { text: 'Google Ads Management', included: false },
        { text: 'SEO Optimization', included: false },
        { text: 'Website Development', included: false },
        { text: 'Advanced Analytics', included: false },
        { text: 'Priority Support', included: false }
      ],
      ctaText: 'Get Started',
      ctaHref: '/contact'
    },
    {
      name: 'Professional',
      price: '2499',
      period: 'month',
      description: 'Ideal for growing businesses that need comprehensive digital marketing',
      isPopular: true,
      features: [
        { text: 'Social Media Management (5 platforms)', included: true },
        { text: 'Content Creation (20 posts/month)', included: true },
        { text: 'Advanced Analytics & Reporting', included: true },
        { text: 'Priority Email Support', included: true },
        { text: 'Bi-weekly Strategy Calls', included: true },
        { text: 'Google Ads Management ($2,000 ad spend)', included: true },
        { text: 'SEO Optimization', included: true },
        { text: 'Email Marketing Campaigns', included: true },
        { text: 'Basic Website Updates', included: true },
        { text: 'Monthly Performance Review', included: true }
      ],
      ctaText: 'Most Popular',
      ctaHref: '/contact'
    },
    {
      name: 'Enterprise',
      price: '4999',
      period: 'month',
      description: 'Complete solution for large businesses with complex needs',
      isPopular: false,
      features: [
        { text: 'Unlimited Social Media Management', included: true },
        { text: 'Unlimited Content Creation', included: true },
        { text: 'Custom Analytics Dashboard', included: true },
        { text: 'Dedicated Account Manager', included: true },
        { text: 'Weekly Strategy Calls', included: true },
        { text: 'Google Ads Management ($5,000 ad spend)', included: true },
        { text: 'Advanced SEO & Content Strategy', included: true },
        { text: 'Custom Email Marketing Automation', included: true },
        { text: 'Full Website Development & Maintenance', included: true },
        { text: '24/7 Priority Support', included: true }
      ],
      ctaText: 'Contact Sales',
      ctaHref: '/contact'
    }
  ];

  const addOnServices = [
    {
      name: 'Website Development',
      price: '2999',
      description: 'Custom website development with modern design and mobile optimization',
      features: ['Responsive Design', 'SEO Optimized', 'Fast Loading', 'Mobile-First']
    },
    {
      name: 'E-commerce Setup',
      price: '3999',
      description: 'Complete e-commerce solution with payment integration and inventory management',
      features: ['Payment Gateway', 'Inventory Management', 'Order Tracking', 'Analytics']
    },
    {
      name: 'Brand Identity Design',
      price: '1999',
      description: 'Complete brand identity including logo, guidelines, and marketing materials',
      features: ['Logo Design', 'Brand Guidelines', 'Business Cards', 'Marketing Materials']
    },
    {
      name: 'Advanced Analytics Setup',
      price: '999',
      description: 'Custom analytics dashboard with advanced tracking and reporting',
      features: ['Custom Dashboard', 'Advanced Tracking', 'Automated Reports', 'Data Insights']
    }
  ];

  const faqs = [
    {
      question: 'What\'s included in the monthly fee?',
      answer: 'All plans include strategy development, content creation, social media management, and regular reporting. Higher tiers include additional services like Google Ads management and website development.'
    },
    {
      question: 'Can I change my plan anytime?',
      answer: 'Yes, you can upgrade or downgrade your plan at any time. Changes will be prorated and take effect in your next billing cycle.'
    },
    {
      question: 'Do you require long-term contracts?',
      answer: 'No, all our plans are month-to-month with no long-term contracts required. You can cancel anytime with 30 days notice.'
    },
    {
      question: 'What if I need custom services?',
      answer: 'We offer custom solutions for unique needs. Contact us to discuss your specific requirements and we\'ll create a tailored package for you.'
    },
    {
      question: 'How do you measure success?',
      answer: 'We track key metrics like website traffic, lead generation, conversion rates, and ROI. You\'ll receive detailed monthly reports showing your progress.'
    },
    {
      question: 'Do you work with businesses in any industry?',
      answer: 'Yes, we work with businesses across all industries including healthcare, technology, retail, professional services, and more.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Pricing Plans - Digital Marketing Services | Subtronz</title>
        <meta name="description" content="Transparent pricing for our digital marketing and web development services. Choose from Starter, Professional, or Enterprise plans to fit your business needs." />
        <meta name="keywords" content="digital marketing pricing, web development cost, social media management pricing, seo services cost" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-orange-50 to-purple-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-secondary mb-6">
              Simple, <span className="gradient-text">Transparent</span> Pricing
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Choose the perfect plan for your business. All plans include our core services 
              with no hidden fees or long-term contracts.
            </p>
            <div className="flex items-center justify-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-2" />
                No Setup Fees
              </div>
              <div className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-2" />
                Cancel Anytime
              </div>
              <div className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-2" />
                30-Day Money Back
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <PricingCard
                key={index}
                {...plan}
                delay={index * 0.1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Add-on Services */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-4">
              Add-on Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Enhance your plan with additional services tailored to your specific needs.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {addOnServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-secondary mb-2">{service.name}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </div>
                  <div className="text-2xl font-bold text-primary">${service.price}</div>
                </div>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button variant="outline" size="sm" className="w-full">
                  Learn More
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Comparison */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-4">
              Compare Plans
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See exactly what's included in each plan to make the best choice for your business.
            </p>
          </motion.div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-xl shadow-lg overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Features</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Starter</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Professional</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Enterprise</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[
                  'Social Media Management',
                  'Content Creation',
                  'Analytics & Reporting',
                  'Email Support',
                  'Strategy Calls',
                  'Google Ads Management',
                  'SEO Optimization',
                  'Website Development',
                  'Email Marketing',
                  'Dedicated Account Manager',
                  'Priority Support'
                ].map((feature, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{feature}</td>
                    <td className="px-6 py-4 text-center">
                      {index < 5 ? (
                        <Check className="w-5 h-5 text-green-500 mx-auto" />
                      ) : (
                        <X className="w-5 h-5 text-gray-300 mx-auto" />
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {index < 9 ? (
                        <Check className="w-5 h-5 text-green-500 mx-auto" />
                      ) : (
                        <X className="w-5 h-5 text-gray-300 mx-auto" />
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-5 h-5 text-green-500 mx-auto" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Got questions? We've got answers. Here are some common questions about our pricing and services.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-lg"
              >
                <h3 className="text-lg font-semibold text-secondary mb-3">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-primary via-accent to-purple">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center text-white"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Choose your plan and start transforming your digital presence today. 
              Need help deciding? Contact us for a free consultation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="secondary" size="lg" href="/contact#contact-form">
                Get Free Consultation
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button variant="outline" size="lg" href="/contact" className="border-white text-white hover:bg-white hover:text-primary">
                Contact Sales
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Pricing;
